import {
  forwardRef,
  Injector,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { FormControl, NgControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';

import { BasicButtonColors, BasicButtonSizes, IconModes } from '@app/shared/enums/buttons';
import { InputDensityEnum, InputModesEnum } from '@app/shared/enums/form-field';
import { UtilService } from '@app/shared/services/utils/util.service';

class CustomFieldErrorMatcher implements ErrorStateMatcher {
  constructor(
    private customControl: FormControl,
    private errors: any
  ) { }
  isErrorState(): boolean {
    return this.customControl && this.customControl.touched && (this.customControl.invalid || this.errors);
  }
}

@Component({
  selector: 'cm-text-field',
  templateUrl: './text-field.component.html',
  styleUrls: [
    './text-field-standard-mode.component.scss',
    './text-field-fill-mode.component.scss',
    './text-field-outline-mode.component.scss',
    './text-field-density.component.scss',
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => TextFieldComponent),
    },
  ],
})
export class TextFieldComponent implements AfterViewInit {
  // Field ElementRef
  @ViewChild('inputEl') inputEl: ElementRef<HTMLInputElement>;

  // Field Inputs & Outputs
  @Input() mode: InputModesEnum = InputModesEnum.STANDARD;
  @Input() density: InputDensityEnum = InputDensityEnum.DEFAULT;
  @Input() label = '';
  @Input() placeholder = '';
  @Input() value: string | number;
  @Input() startDate: Date = null;
  @Input() endDate: Date = null;
  @Input() errorMessage = '';
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() dataCy: string;
  @Input() required = false;
  @Input() min = 0;
  @Input() max = 255;
  @Input() maxLength = 255;
  @Input() autofocus = false;
  @Input() rows: number;
  @Input() textareaAutosize = false;
  @Input() showMaxLength: boolean;
  @Input() setAutofocus: boolean;
  @Input() public type: string;
  @Input() isFormField: boolean;
  @Input() public hint: string;
  @Input() public minDate: Date;
  @Input() public maxDate: Date;
  @Input() public name: string;

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onFocus = new EventEmitter<any>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onBlur = new EventEmitter<any>();
  @Output() valueChange = new EventEmitter<any>();
  @Output() startDateChange = new EventEmitter<any>();
  @Output() endDateChange = new EventEmitter<any>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onEnter = new EventEmitter<any>();

  // Preffix Inputs & Outputs
  @Input() leftIcon = '';
  @Input() leftIconHasRipple = true;
  @Input() leftIconIconMode: IconModes = IconModes.FILLED;
  @Input() leftIconColor: BasicButtonColors = BasicButtonColors.SECONDARY;
  @Input() leftIconSize: BasicButtonSizes = BasicButtonSizes.MEDIUM;
  @Input() defaultValue: any;
  @Output() leftIconOnClick = new EventEmitter<any>();

  // Suffix Inputs & Outputs
  @Input() rightIcon = '';
  @Input() rightIconHasRipple = true;
  @Input() rightIconMode: IconModes = IconModes.FILLED;
  @Input() rightIconColor: BasicButtonColors = BasicButtonColors.SECONDARY;
  @Input() rightIconSize: BasicButtonSizes = BasicButtonSizes.MEDIUM;

  @Input() rightIndicator = '';
  @Input() rightIndicatorClass = '';

  @Output() rightIconOnClick = new EventEmitter<any>();

  @Input() errors: any = null;

  @Input() minErrorMessage: string;

  @Input() tooltip?: string;

  public control: FormControl;
  public onChange: any = () => { };
  public onTouched: any = () => { };
  touched = false;

  public infoPopoverOpen = false;
  public isMobileView = false;

  constructor(
    public injector: Injector,
    private cdref: ChangeDetectorRef
  ) {
    this.onResize();
  }

  @HostListener('window:resize', ['$event']) onResize() {
    this.isMobileView = UtilService.isMobileAndTabletSize();
  }

  valueChanged(value: string): void {
    setTimeout(() => {
      this.value = value;
      this.valueChange.emit(value);
      this.cdref.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    this.autofocus && this.setAutoFocus();
    const ngControl: NgControl = this.injector.get(NgControl, null);
    if (ngControl) {
      setTimeout(() => {
        this.control = ngControl.control as FormControl;
      });
    }
  }

  setAutoFocus(): void {
    this.inputEl.nativeElement.focus();
    this.cdref.detectChanges();
  }

  emitOnBlur($event) {
    this.onBlur.emit($event);
    this.value = !this.value ? this.defaultValue : this.value;
  }

  errorMatcher() {
    return new CustomFieldErrorMatcher(this.control, this.errors);
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  focusIn(event: any) {
    this.touched = true;
    this.onFocus.emit(event);
  }

  onStartDateChange(event: any) {
    this.startDate = event;
    this.startDateChange.emit(this.startDate);
  }

  onEndDateChange(event: any) {
    this.endDate = event;
    this.endDateChange.emit(this.endDate);
  }

  blur(event: any) {
    if (this.control) {
      this.control.markAsTouched();
    }
    this.onBlur.emit(event);
  }

  get tabIndex() {
    return this.setAutofocus === false ? -1 : 0;
  }

  get hasControlErrors(): boolean {
    return !this.disabled && this.control?.errors && this.touched;
  }
}
